/**
 * VsIntroOverlay — the 2D layer of the pre-buy "VS" cinematic. It sits on top
 * of the in-arena shot of the two fighters (rendered by {@link VsIntro}) and
 * shows each side's avatar + name + faction, a central "VS", and the intro
 * countdown. Non-interactive (pointer-events off; auto-dismissed when the
 * intro phase ends). CT is drawn on the left, T on the right — matching the
 * staged 3D fighters on the platform.
 */
export interface VsSideInfo {
  name: string;
  /** Uploaded profile photo URL; falls back to a colored initial. */
  avatarUrl?: string;
  /** Tags the local player's side with a small "You" chip. */
  you?: boolean;
}

function esc(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string),
  );
}

export class VsIntroOverlay {
  private root: HTMLDivElement;
  private countEl: HTMLElement | null = null;
  private lastSec = -1;

  constructor() {
    const el = document.createElement("div");
    el.id = "vs-intro-overlay";
    el.style.cssText = [
      "position:fixed",
      "inset:0",
      "z-index:9998",
      "display:none",
      "pointer-events:none",
      "overflow:hidden",
      "font-family:'Inter',-apple-system,BlinkMacSystemFont,sans-serif",
    ].join(";");
    document.body.appendChild(el);
    this.root = el;
  }

  show(ct: VsSideInfo, t: VsSideInfo): void {
    this.lastSec = -1;
    this.root.innerHTML = `
      <style>
        #vs-intro-overlay .vsi-grad{position:absolute;inset:0;background:linear-gradient(180deg,
          rgba(8,8,12,0.55) 0%, rgba(8,8,12,0) 24%, rgba(8,8,12,0) 58%, rgba(8,8,12,0.78) 100%);}
        #vs-intro-overlay .vsi-top{position:absolute;top:6%;left:0;right:0;text-align:center;}
        #vs-intro-overlay .vsi-ready{font-size:13px;letter-spacing:4px;text-transform:uppercase;
          color:#cfd6e6;margin-bottom:6px;font-family:'Inter',ui-sans-serif,sans-serif;
          animation:vsi-fade .6s ease both;}
        #vs-intro-overlay .vsi-count{font-size:72px;font-weight:800;color:#fff;line-height:1;
          text-shadow:0 4px 24px rgba(0,0,0,0.6);font-variant-numeric:tabular-nums;}
        #vs-intro-overlay .vsi-vs{position:absolute;left:50%;top:45%;transform:translate(-50%,-50%);
          font-size:84px;font-weight:800;font-style:italic;color:#fff;letter-spacing:2px;
          text-shadow:0 0 30px rgba(0,0,0,0.55),0 6px 20px rgba(0,0,0,0.5);
          animation:vsi-vsin .7s cubic-bezier(.2,.9,.25,1) both;}
        #vs-intro-overlay .vsi-card{position:absolute;bottom:8%;display:flex;align-items:center;gap:16px;}
        #vs-intro-overlay .vsi-ct{left:6%;animation:vsi-inL .6s ease both;}
        #vs-intro-overlay .vsi-t{right:6%;flex-direction:row-reverse;text-align:right;
          animation:vsi-inR .6s ease both;}
        #vs-intro-overlay .vsi-av{width:72px;height:72px;border-radius:50%;overflow:hidden;flex-shrink:0;
          display:flex;align-items:center;justify-content:center;background:#1a1d27;
          box-shadow:0 6px 22px rgba(0,0,0,0.5);}
        #vs-intro-overlay .vsi-ct .vsi-av{border:3px solid #5b8def;}
        #vs-intro-overlay .vsi-t .vsi-av{border:3px solid #ff7a33;}
        #vs-intro-overlay .vsi-av img{width:100%;height:100%;object-fit:cover;display:block;}
        #vs-intro-overlay .vsi-av-fb{font-size:30px;font-weight:700;color:#fff;}
        #vs-intro-overlay .vsi-meta{min-width:0;}
        #vs-intro-overlay .vsi-name{font-size:24px;font-weight:700;color:#fff;letter-spacing:.3px;
          text-shadow:0 2px 10px rgba(0,0,0,0.6);display:flex;align-items:center;gap:10px;}
        #vs-intro-overlay .vsi-t .vsi-name{flex-direction:row-reverse;}
        #vs-intro-overlay .vsi-fac{font-size:12px;letter-spacing:3px;text-transform:uppercase;margin-top:5px;
          font-family:'Inter',ui-sans-serif,sans-serif;}
        #vs-intro-overlay .vsi-ct .vsi-fac{color:#5b8def;}
        #vs-intro-overlay .vsi-t .vsi-fac{color:#ff7a33;}
        #vs-intro-overlay .vsi-you{font-size:11px;font-weight:700;letter-spacing:1.5px;color:#0a0d13;
          background:#e8edf5;border-radius:999px;padding:2px 8px;}
        @keyframes vsi-pop{0%{transform:scale(1.5);opacity:.4}100%{transform:scale(1);opacity:1}}
        @keyframes vsi-vsin{0%{opacity:0;transform:translate(-50%,-50%) scale(2.2)}
          100%{opacity:1;transform:translate(-50%,-50%) scale(1)}}
        @keyframes vsi-inL{0%{opacity:0;transform:translateX(-40px)}100%{opacity:1;transform:translateX(0)}}
        @keyframes vsi-inR{0%{opacity:0;transform:translateX(40px)}100%{opacity:1;transform:translateX(0)}}
        @keyframes vsi-fade{0%{opacity:0}100%{opacity:1}}
      </style>
      <div class="vsi-grad"></div>
      <div class="vsi-top">
        <div class="vsi-ready">Match found — get ready</div>
        <div class="vsi-count">${this.lastSec >= 0 ? this.lastSec : ""}</div>
      </div>
      <div class="vsi-vs">VS</div>
      ${this.card(ct, "ct")}
      ${this.card(t, "t")}
    `;
    this.countEl = this.root.querySelector(".vsi-count");
    this.root.style.display = "block";
  }

  /** Update the big countdown number (seconds left). Re-pops on each change. */
  setCountdown(sec: number): void {
    const s = Math.max(0, Math.floor(sec));
    if (!this.countEl || s === this.lastSec) return;
    this.lastSec = s;
    this.countEl.textContent = String(s);
    this.countEl.style.animation = "none";
    void this.countEl.offsetWidth; // reflow so the pop animation retriggers
    this.countEl.style.animation = "vsi-pop .4s ease";
  }

  hide(): void {
    this.root.style.display = "none";
    this.root.innerHTML = "";
    this.countEl = null;
  }

  private card(info: VsSideInfo, team: "ct" | "t"): string {
    const initial = esc((info.name.trim()[0] || "?").toUpperCase());
    const av = info.avatarUrl
      ? `<div class="vsi-av"><img src="${esc(info.avatarUrl)}" alt=""/></div>`
      : `<div class="vsi-av vsi-av-fb">${initial}</div>`;
    const faction = team === "ct" ? "Counter-Terrorist" : "Terrorist";
    const you = info.you ? `<span class="vsi-you">You</span>` : "";
    return `
      <div class="vsi-card vsi-${team}">
        ${av}
        <div class="vsi-meta">
          <div class="vsi-name">${esc(info.name)}${you}</div>
          <div class="vsi-fac">${faction}</div>
        </div>
      </div>`;
  }
}
